import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { checkCareerTitleExists } from '../../../api/career/career.api';
import { ReactComponent as EditIcon20 } from '@repo/common/assets/icons/20px/icon_20px_edit_outline.svg';

const ResumeTitle = ({ title, setTitle, resumeData }) => {
  const { resumeId } = useParams();
  const [isFocused, setIsFocused] = useState(false);
  const [placeholderText, setPlaceholderText] = useState('');

  const inputRef = useRef(null);
  const measureRef = useRef(null);

  useEffect(() => {
    if (title) return;

    if (resumeId && resumeId !== 'draft') return;

    const profile = Array.isArray(resumeData)
      ? resumeData[0] || {}
      : resumeData || {};

    const basePlaceholder = `${profile.firstName ?? 'Firstname'}_${
      profile.lastName ?? 'Lastname'
    }_Resume_Draft`;

    const generateUniquePlaceholder = async () => {
      let unique = basePlaceholder;
      let count = 0;

      while (await checkCareerTitleExists(unique)) {
        count += 1;
        unique = `${basePlaceholder}(${count})`;
      }

      setPlaceholderText(unique);
    };

    generateUniquePlaceholder();
  }, [resumeId, title, resumeData]);

  const measureText = title || placeholderText;

  useLayoutEffect(() => {
    if (!inputRef.current || !measureRef.current) return;

    const textWidth = measureRef.current.offsetWidth;
    const padding = 28;

    let finalWidth;
    if (isFocused && !title) {
      finalWidth = 33;
    } else {
      finalWidth = Math.max(textWidth + padding, 33);
    }

    inputRef.current.style.width = `${Math.min(finalWidth, 1330)}px`;
  }, [measureText, isFocused, title]);

  return (
    <div className='group relative flex min-w-0 max-w-[320px] items-center justify-center rounded-[99px] bg-grayscale-gray00 tabletH:max-w-[1200px] tabletH:justify-start'>
      <span
        ref={measureRef}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          whiteSpace: 'pre',
          font: 'inherit',
          letterSpacing: 'inherit',
        }}
      >
        {measureText}
      </span>

      <input
        ref={inputRef}
        id='resume-title-input'
        value={title}
        placeholder={isFocused ? '' : placeholderText}
        onChange={(e) => setTitle(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={`
          min-w-0
          max-w-full
          px-0 py-0 tabletH:pl-4 tabletH:pr-3 tabletH:py-[9px] desktopL:py-[7px]
          rounded-[99px]
          outline-none
          whitespace-nowrap
          overflow-hidden text-ellipsis
          bg-transparent
          border
          typo-b14 tabletH:typo-b14 desktopL:typo-b16
          text-center tabletH:text-left
          text-text-primary
          placeholder:text-text-placeholder
          transition-[border-color]
          ${
            isFocused
              ? 'border-grayscale-gray07 bg-grayscale-gray00'
              : 'border-transparent tabletH:group-hover:border-grayscale-gray03 cursor-pointer'
          }
        `}
      />
      <span className='flex size-6 shrink-0 items-center justify-center tabletH:hidden'>
        <EditIcon20 className='size-5 text-grayscale-gray07' />
      </span>
    </div>
  );
};

export default ResumeTitle;
